import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Button, Space, Typography, Tag, Upload, Modal, message, Alert, Grid, Spin,
} from 'antd';
import {
  DownloadOutlined, UploadOutlined, FileTextOutlined, WarningOutlined, EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { saludSatService } from '../../services/api';
import SemaforoOpinion from './SemaforoOpinion';
import { antiguedad, tieneMasDeUnMes } from '../../utils/pdfTexto';

const { Text } = Typography;

const DOCUMENTOS = {
  csf: {
    titulo: 'Constancia de situación fiscal',
    archivo: 'constancia-situacion-fiscal',
    falta: 'Aún no se ha cargado la constancia de situación fiscal de este RFC.',
  },
  opinion: {
    titulo: 'Opinión de cumplimiento',
    archivo: 'opinion-cumplimiento',
    falta: 'Aún no se ha cargado la opinión de cumplimiento de este RFC.',
  },
};

/**
 * Salud SAT: constancia de situación fiscal y opinión de cumplimiento.
 *
 * Son el estado del RFC, no el resultado de un periodo, así que no hay selector
 * de mes ni de ejercicio: cada documento existe o no existe, y ya.
 *
 * La sección se pinta SIEMPRE, incluso vacía. Un panel que se esconde cuando no
 * hay nada no le enseña a nadie que el documento existe y se puede pedir; por
 * eso el hueco es contenido y dice qué falta.
 *
 * Un mismo componente sirve al contribuyente y al fiscal-admin. `puede_cargar`
 * lo decide el servidor y llega en la respuesta: deducir aquí el rol daría dos
 * verdades sobre lo mismo, y la de la interfaz no es la que manda.
 *
 * `permitirCarga` es el filtro del anfitrión, no un permiso: el perfil del
 * contribuyente lo apaga para que ahí NUNCA aparezca el control, ni siquiera
 * cuando quien mira es un Admin. Cargar es un acto del panel fiscal.
 */
const SaludSatPanel = ({ empresa, permitirCarga = true }) => {
  const esMovil = !Grid.useBreakpoint().md;
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [subiendo, setSubiendo] = useState(null);
  const [error, setError] = useState('');
  // Lectura de la opinión: estado propio para que interpretar el PDF no
  // retrase la lista de documentos, que ya está lista mucho antes.
  const [lectura, setLectura] = useState(null);
  const [leyendo, setLeyendo] = useState(false);
  // La constancia se interpreta aparte y en paralelo: fechar e identificar, no
  // juzgar. No produce veredicto de cumplimiento.
  const [lecturaCsf, setLecturaCsf] = useState(null);
  // Visor: { url, titulo }. El url es un blob que HAY que revocar.
  const [visor, setVisor] = useState(null);
  const [abriendoVisor, setAbriendoVisor] = useState(null);

  const companyId = empresa?.id;

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setCargando(true);
    try {
      setEstado(await saludSatService.listByCompany(companyId));
      setError('');
    } catch (err) {
      setError('No se pudo cargar la información de Salud SAT');
    } finally {
      setCargando(false);
    }
  }, [companyId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const hayOpinion = !!(estado?.documentos || []).find((d) => d.kind === 'opinion' && d.cargado);
  // `cargado_en` en las dependencias: reemplazar la opinión tiene que volver a
  // interpretarla, o el semáforo seguiría describiendo al documento anterior.
  const opinionCargadaEn = (estado?.documentos || [])
    .find((d) => d.kind === 'opinion')?.cargado_en;

  useEffect(() => {
    let vigente = true;

    if (!companyId || !hayOpinion) {
      setLectura(null);
      return undefined;
    }

    const leer = async () => {
      setLeyendo(true);
      try {
        // Import dinámico: pdf.js solo lo paga quien abre esta sección.
        const { interpretarOpinion } = await import('../../utils/opinionCumplimiento');
        const respuesta = await saludSatService.download(companyId, 'opinion');
        const resultado = await interpretarOpinion(respuesta.data);
        if (vigente) setLectura(resultado);
      } catch (err) {
        // No se pudo leer no es lo mismo que estar mal con el SAT: el semáforo
        // lo dirá así, nunca en rojo.
        if (vigente) setLectura(null);
      } finally {
        if (vigente) setLeyendo(false);
      }
    };

    leer();

    return () => { vigente = false; };
  }, [companyId, hayOpinion, opinionCargadaEn]);

  const hayCsf = !!(estado?.documentos || []).find((d) => d.kind === 'csf' && d.cargado);
  const csfCargadaEn = (estado?.documentos || []).find((d) => d.kind === 'csf')?.cargado_en;

  useEffect(() => {
    let vigente = true;

    if (!companyId || !hayCsf) {
      setLecturaCsf(null);
      return undefined;
    }

    const leer = async () => {
      try {
        const { interpretarConstancia } = await import('../../utils/constanciaSituacionFiscal');
        const respuesta = await saludSatService.download(companyId, 'csf');
        const resultado = await interpretarConstancia(respuesta.data);
        if (vigente) setLecturaCsf(resultado);
      } catch (err) {
        // Sin interpretación la constancia sigue descargándose y viéndose: la
        // lectura es una ayuda, el documento es la evidencia.
        if (vigente) setLecturaCsf(null);
      }
    };

    leer();

    return () => { vigente = false; };
  }, [companyId, hayCsf, csfCargadaEn]);

  // Un blob URL sin revocar retiene el PDF completo mientras viva la pestaña.
  useEffect(() => () => {
    if (visor?.url) window.URL.revokeObjectURL(visor.url);
  }, [visor?.url]);

  /**
   * Ver sin descargar.
   *
   * El endpoint exige Authorization, así que NO se puede apuntar un iframe a su
   * URL —sería un 401—: hay que traer el blob y mostrarlo desde memoria.
   *
   * Este camino no interpreta nada: no llama a interpretarOpinion ni toca
   * `lectura`. El veredicto vive en un solo lugar.
   */
  const ver = async (kind) => {
    setAbriendoVisor(kind);
    try {
      const response = await saludSatService.download(companyId, kind);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));

      // Revocar el anterior antes de reemplazarlo: ver varios documentos
      // seguidos no debe ir acumulando blobs en memoria.
      if (visor?.url) window.URL.revokeObjectURL(visor.url);

      setVisor({ url, titulo: DOCUMENTOS[kind]?.titulo || kind });
    } catch (err) {
      message.error('No se pudo abrir el documento');
    } finally {
      setAbriendoVisor(null);
    }
  };

  const cerrarVisor = () => {
    if (visor?.url) window.URL.revokeObjectURL(visor.url);
    setVisor(null);
  };

  const descargar = async (kind) => {
    try {
      const response = await saludSatService.download(companyId, kind);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${DOCUMENTOS[kind].archivo}-${empresa.tax_id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error('No se pudo descargar el documento');
    }
  };

  const subir = async (kind, archivo) => {
    setSubiendo(kind);
    try {
      await saludSatService.upload(companyId, kind, archivo);
      message.success('Documento cargado');
      await cargar();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo cargar el documento');
    } finally {
      setSubiendo(null);
    }
  };

  const documentos = estado?.documentos || [];
  const puedeCargar = permitirCarga && !!estado?.puede_cargar;
  const faltaConstancia = documentos.some((d) => d.kind === 'csf' && !d.cargado);
  const csfVieja = tieneMasDeUnMes(lecturaCsf?.emitidaEn);
  const csfDeOtroRfc = !!(
    lecturaCsf?.rfc && empresa?.tax_id && lecturaCsf.rfc !== empresa.tax_id
  );

  /**
   * Con documento vigente el botón dice "Reemplazar" y confirma DESPUÉS de
   * elegir el archivo: la carga nueva borra la anterior y no hay historial del
   * que rescatarla. Confirmar en beforeUpload y no antes deja la confirmación
   * pegada al archivo concreto que se va a subir.
   */
  const control = (doc) => (
    <Upload
      accept="application/pdf"
      maxCount={1}
      showUploadList={false}
      beforeUpload={(archivo) => {
        if (!doc.cargado) {
          subir(doc.kind, archivo);
          return false;
        }

        Modal.confirm({
          title: '¿Reemplazar el documento?',
          content: `Se guardará "${archivo.name}" y el documento actual se borrará. No hay historial: no se puede recuperar.`,
          okText: 'Reemplazar',
          cancelText: 'Cancelar',
          onOk: () => subir(doc.kind, archivo),
        });

        return false;
      }}
    >
      <Button icon={<UploadOutlined />} loading={subiendo === doc.kind} block={esMovil}>
        {doc.cargado ? 'Reemplazar' : 'Cargar'}
      </Button>
    </Upload>
  );

  return (
    <Spin spinning={cargando}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/*
          Recargar es una acción de la SECCIÓN, no de un documento: la consulta
          trae los dos. Un botón por fila sugeriría que se puede refrescar uno
          solo, que no es cierto.

          Llama a `cargar()` y a nada más. Las dos interpretaciones dependen de
          `cargado_en` y reaccionan solas, así que un documento que no cambió NO
          se vuelve a descargar ni a leer. Forzarlas desde aquí sería un segundo
          camino para lo mismo y pagaría dos PDF en cada pulsación.

          `loading` ya bloquea el click en antd, pero solo en JavaScript: el
          botón sigue habilitado en el DOM. `disabled` va aparte para que quede
          inutilizable de verdad —también para quien navega con teclado o con
          lector de pantalla—, sin otro estado que sincronizar: los dos salen
          del mismo `cargando`.
        */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            icon={<ReloadOutlined />}
            onClick={cargar}
            loading={cargando}
            disabled={cargando}
            block={esMovil}
          >
            Recargar
          </Button>
        </div>

        {/* Convive con el contenido en vez de reemplazarlo. Sustituyendo el
            panel entero, un fallo de red puntual se llevaba por delante el
            botón con el que reintentar. */}
        {error && <Alert type="error" message={error} showIcon />}

        {/* El veredicto va primero: es lo que la persona vino a saber.
            Se condiciona a `estado` y no a `!cargando` para que al recargar no
            parpadee: lo que ya se sabía sigue en pantalla mientras se consulta. */}
        {estado && (
          <SemaforoOpinion
            lectura={lectura}
            cargando={leyendo}
            hayOpinion={hayOpinion}
            rfcEmpresa={empresa?.tax_id}
          />
        )}

        {/* La falta de opinión ya la dice el semáforo; este aviso cubre solo la
            constancia para no decir dos veces lo mismo. */}
        {estado && faltaConstancia && !puedeCargar && (
          <Alert
            type="info"
            showIcon
            icon={<WarningOutlined />}
            message="Tu constancia de situación fiscal no está cargada"
            description="La carga tu gestor fiscal desde el portal del SAT. Pídesela si la necesitas."
          />
        )}

        {documentos.map((doc) => {
          const meta = DOCUMENTOS[doc.kind] || { titulo: doc.kind, falta: 'Sin cargar.' };

          return (
            <Card key={doc.kind} size="small">
              <Space
                direction={esMovil ? 'vertical' : 'horizontal'}
                align={esMovil ? 'start' : 'center'}
                style={{ width: '100%', justifyContent: 'space-between', display: 'flex' }}
                size="middle"
              >
                <Space direction="vertical" size={2}>
                  <Space>
                    <FileTextOutlined />
                    <Text strong>{meta.titulo}</Text>
                    {doc.cargado
                      ? <Tag color="green">Cargada</Tag>
                      : <Tag>Sin cargar</Tag>}
                  </Space>

                  {doc.cargado ? (
                    <Space direction="vertical" size={0}>
                      {/* Emisión y carga son cosas distintas, y la diferencia
                          entre ambas dice algo: una constancia emitida en
                          febrero y subida en agosto habla de cómo se está
                          operando la cuenta. */}
                      {doc.kind === 'csf' && lecturaCsf?.emitidaEn && (
                        <Text style={{ fontSize: 12 }}>
                          Emitida el{' '}
                          {lecturaCsf.emitidaEn.toLocaleDateString('es-MX', {
                            day: 'numeric', month: 'short', year: 'numeric',
                          })}
                          {' · '}
                          {antiguedad(lecturaCsf.emitidaEn)}
                        </Text>
                      )}
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Cargada el {doc.cargado_en}
                        {doc.cargado_por?.name ? ` por ${doc.cargado_por.name}` : ''}
                      </Text>
                    </Space>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 12 }}>{meta.falta}</Text>
                  )}
                </Space>

                <Space wrap>
                  {/* Ver acompaña a Descargar y NO depende de permitirCarga:
                      esa prop gobierna cargar, que es otra cosa. */}
                  {doc.cargado && (
                    <Button
                      icon={<EyeOutlined />}
                      onClick={() => ver(doc.kind)}
                      loading={abriendoVisor === doc.kind}
                      block={esMovil}
                    >
                      Ver
                    </Button>
                  )}
                  {doc.cargado && (
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={() => descargar(doc.kind)}
                      block={esMovil}
                    >
                      Descargar
                    </Button>
                  )}
                  {puedeCargar && control(doc)}
                </Space>
              </Space>

              {/* Avisos de la constancia. Recomiendan, no declaran caducidad:
                  el SAT no imprime una en el documento. */}
              {doc.kind === 'csf' && doc.cargado && csfVieja && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 12 }}
                  message={`Esta constancia se emitió ${antiguedad(lecturaCsf.emitidaEn)}`}
                  description="Puede no reflejar el régimen, el domicilio ni las obligaciones actuales. Conviene descargar una actualizada del portal del SAT."
                />
              )}

              {doc.kind === 'csf' && doc.cargado && csfDeOtroRfc && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginTop: 12 }}
                  message="Esta constancia es de otro RFC"
                  description={`El documento cargado corresponde a ${lecturaCsf.rfc}, no a ${empresa?.tax_id}. Hay que reemplazarlo por el correcto.`}
                />
              )}
            </Card>
          );
        })}

        <Text type="secondary" style={{ fontSize: 12 }}>
          Estos documentos no dependen del periodo: reflejan el estado del RFC ante el SAT.
          Se conserva solo el más reciente de cada uno.
        </Text>

        {/* El PDF vive en un blob en memoria, no en una URL con token. */}
        <Modal
          title={visor?.titulo || 'Documento'}
          open={!!visor}
          onCancel={cerrarVisor}
          footer={
            <Space>
              {visor?.url && (
                <Button onClick={() => window.open(visor.url, '_blank', 'noopener')}>
                  Abrir en pestaña nueva
                </Button>
              )}
              <Button onClick={cerrarVisor}>Cerrar</Button>
            </Space>
          }
          width={esMovil ? '100%' : 900}
          style={esMovil ? { top: 8, maxWidth: '100vw', margin: 0, padding: 0 } : undefined}
          styles={{ body: { padding: 0 } }}
        >
          {visor?.url && (
            <iframe
              src={visor.url}
              // Con el nombre del documento, no un genérico: es lo que anuncia
              // un lector de pantalla al entrar al marco.
              title={visor.titulo}
              style={{ width: '100%', height: '70vh', border: 0 }}
            />
          )}
          <div style={{ padding: '8px 16px', fontSize: 12 }}>
            <Text type="secondary">
              Si el PDF no se muestra, usa “Abrir en pestaña nueva”: algunos navegadores
              móviles no lo incrustan.
            </Text>
          </div>
        </Modal>
      </Space>
    </Spin>
  );
};

export { DOCUMENTOS };
export default SaludSatPanel;
